<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import ExternalMessagesAPI from 'dashboard/api/externalMessages';
import ConnectedBanks from '../../bankQueries/ConnectedBanks.vue';
import NextDialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ContactDeleteModal from 'dashboard/modules/contact/ContactDeleteModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    ContactDeleteModal,
    VoiceCallButton,
    InlineInput,
    ConnectedBanks,
    NextDialog,
    NextSelect,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    enableBankSimulation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      isEditingName: false,
      editName: '',
      labelCpf: 'CPF',
      labelRegistrationNumber: 'Matrícula',
      bankSimulationTitle: 'Simulação do banco',
      bankSimulationDescription:
        'Consulte os bancos com o mesmo fluxo da Consulta CLT: autorizações, dados adicionais e ofertas.',
      bankSimulationMessageLabel: 'Mensagem para o contato',
      bankSimulationMissingPhone:
        'Este contato precisa ter um telefone cadastrado para receber a mensagem.',
      bankSimulationMissingInbox:
        'Nenhuma caixa WhatsApp ou SMS disponível para enviar a mensagem.',
      bankSimulationNoSuccess:
        'A mensagem fica disponível quando houver uma oferta com valor liberado positivo para este contato.',
      bankSimulationCancelLabel: 'Fechar',
      bankSimulationSendLabel: 'Enviar mensagem para o contato',
      bankSimulationOfferLabel: 'Simulação para enviar',
      showBankSimulationModal: false,
      bankSimulationBatch: null,
      selectedBankSimulationOfferKey: '',
      isConsultingBankSimulation: false,
      isSendingBankSimulation: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
      inboxes: 'inboxes/getInboxes',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
    contactCpf() {
      return this.contact.talkvia_profile?.cpf || '';
    },
    bankSimulationConversationId() {
      const contactId =
        this.currentChat?.meta?.sender?.id || this.currentChat?.contact_id;
      return Number(contactId) === Number(this.contact.id)
        ? this.currentChat?.id
        : null;
    },
    bankSimulationOfferOptions() {
      return this.successfulBankSimulationResults.flatMap(
        (result, resultIndex) =>
          (result.offers || [])
            .filter(offer => Number(offer.amount) > 0)
            .map(offer => ({
              value: JSON.stringify([
                this.bankSimulationBatch?.id,
                result.id ?? resultIndex,
                offer,
              ]),
              label: `${result.provider_label} · ${this.bankSimulationCurrency(offer.amount)}${offer.term ? ` · ${offer.term} parcelas` : ''}`,
              bank: result.provider_label,
              offer,
            }))
      );
    },
    bankSimulationOfferValue: {
      get() {
        return (
          this.selectedBankSimulationOfferKey ||
          this.bankSimulationOfferOptions[0]?.value ||
          ''
        );
      },
      set(value) {
        this.selectedBankSimulationOfferKey = value;
      },
    },
    selectedBankSimulationOffer() {
      return this.bankSimulationOfferOptions.find(
        option => option.value === this.bankSimulationOfferValue
      );
    },
    bankSimulationMessage() {
      const selected = this.selectedBankSimulationOffer;
      if (!selected) return '';
      const { offer, bank } = selected;
      const lines = [
        `Olá ${this.contact.name || 'cliente'}, encontramos uma oferta no ${bank}.`,
        '',
        `Valor liberado: ${this.bankSimulationCurrency(offer.amount)}`,
      ];
      if (offer.term) lines.push(`Quantidade de parcelas: ${offer.term}`);
      if (offer.installment)
        lines.push(
          `Valor da parcela: ${this.bankSimulationCurrency(offer.installment)}`
        );
      if (offer.rate)
        lines.push(`Taxa: ${String(offer.rate).replace('.', ',')}%`);
      lines.push(
        '',
        'Podemos conversar sobre as condições e os próximos passos?',
        'A simulação não garante a contratação.'
      );
      return lines.join('\n');
    },
    successfulBankSimulationResults() {
      if (
        Number(this.bankSimulationBatch?.contact_id) !== Number(this.contact.id)
      )
        return [];
      return (this.bankSimulationBatch?.results || []).filter(
        result =>
          result.status === 'success' &&
          result.offers?.some(offer => Number(offer.amount) > 0)
      );
    },
    hasSuccessfulBankSimulation() {
      return !!this.selectedBankSimulationOffer;
    },
    supportedBankSimulationInboxes() {
      return this.inboxes.filter(inbox => {
        const isWhatsApp = inbox.channel_type === INBOX_TYPES.WHATSAPP;
        const isSms = inbox.channel_type === INBOX_TYPES.SMS;
        const isTwilioSms =
          inbox.channel_type === INBOX_TYPES.TWILIO && inbox.medium === 'sms';

        return isWhatsApp || isSms || isTwilioSms;
      });
    },
    bankSimulationInbox() {
      const currentInbox = this.supportedBankSimulationInboxes.find(
        inbox => Number(inbox.id) === Number(this.currentChat?.inbox_id)
      );

      return currentInbox || this.supportedBankSimulationInboxes[0] || null;
    },
    canSendBankSimulation() {
      return (
        !!this.contact.phone_number &&
        !!this.bankSimulationInbox &&
        this.hasSuccessfulBankSimulation &&
        !this.isConsultingBankSimulation &&
        !this.isSendingBankSimulation
      );
    },
  },
  watch: {
    'bankSimulationBatch.id'() {
      this.selectedBankSimulationOfferKey = '';
    },
    'contact.id': {
      handler(id) {
        this.$refs.bankSimulationDialog?.close();
        this.showBankSimulationModal = false;
        this.bankSimulationBatch = null;
        this.selectedBankSimulationOfferKey = '';
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    bankSimulationCurrency(value) {
      const text = String(value);
      const amount = Number(
        text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text
      );
      return amount.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    },
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    startEditingName() {
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    saveNameEdit() {
      if (!this.isEditingName) return;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
    async copyName() {
      try {
        await copyTextToClipboard(this.contact.name);
        useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      } catch {
        useAlert(this.$t('CONTACT_PANEL.COPY_FAILED') || 'Erro ao copiar');
      }
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async openBankSimulationModal() {
      if (!this.enableBankSimulation) return;
      this.bankSimulationBatch = null;
      this.selectedBankSimulationOfferKey = '';
      this.isConsultingBankSimulation = false;
      this.showBankSimulationModal = true;
      this.$nextTick(() => this.$refs.bankSimulationDialog.open());
    },
    closeBankSimulationModal() {
      this.showBankSimulationModal = false;
      this.bankSimulationBatch = null;
      this.selectedBankSimulationOfferKey = '';
    },
    async sendBankSimulationMessage() {
      if (!this.canSendBankSimulation) return;

      this.isSendingBankSimulation = true;
      try {
        await ExternalMessagesAPI.create({
          inbox_id: this.bankSimulationInbox.id,
          phone_number: this.contact.phone_number,
          name: this.contact.name,
          content: this.bankSimulationMessage,
          content_type: 'text',
          status: 'open',
        });
        useAlert('Simulação enviada com sucesso.');
        this.$refs.bankSimulationDialog.close();
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Não foi possível enviar a simulação.';
        useAlert(message);
      } finally {
        this.isSendingBankSimulation = false;
      }
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <div class="relative w-full bg-n-solid-2 px-2 pb-3 pt-2">
    <div
      class="grid w-full gap-x-3 gap-y-3 rounded-2xl border border-n-weak bg-n-solid-1 p-3 text-left shadow-sm rtl:text-right"
      :class="showAvatar ? 'grid-cols-[3rem_minmax(0,1fr)]' : 'grid-cols-1'"
    >
      <div v-if="showAvatar" class="pt-0.5">
        <Avatar
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
        />
      </div>

      <div class="flex min-w-0 w-full flex-col items-start gap-1.5">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <InlineInput
            v-if="isEditingName"
            ref="nameInput"
            v-model="editName"
            custom-input-class="!text-base !font-medium"
            class="!w-fit"
            @enter-press="saveNameEdit"
            @escape-press="cancelNameEdit"
            @blur="saveNameEdit"
          />
          <h3
            v-else
            class="group/name flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12 cursor-pointer hover:text-n-slate-12/80"
            :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
            @click="startEditingName"
          >
            {{ contact.name }}
            <span
              class="i-lucide-pencil text-xs text-n-slate-10 opacity-0 group-hover/name:opacity-100 transition-opacity ml-1 align-middle"
            />
          </h3>
          <div class="flex flex-row items-center gap-2">
            <NextButton
              v-if="contact.name"
              v-tooltip.top="'Copiar nome'"
              ghost
              xs
              slate
              icon="i-lucide-clipboard"
              @click="copyName"
            />
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
            editable
            @update="value => onFieldUpdate('phone_number', value)"
          />
          <ContactInfoRow
            v-if="contact.talkvia_profile && contact.talkvia_profile.cpf"
            :value="contact.talkvia_profile.cpf"
            icon="contact-card"
            emoji="🪪"
            :title="labelCpf"
            show-copy
          />
          <ContactInfoRow
            v-if="
              contact.talkvia_profile &&
              contact.talkvia_profile.registration_number
            "
            :value="contact.talkvia_profile.registration_number"
            icon="number-symbol"
            emoji="#️⃣"
            :title="labelRegistrationNumber"
            show-copy
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div
        class="col-span-full flex w-full items-center gap-1.5 rounded-xl border border-n-weak bg-n-alpha-1 p-1.5"
      >
        <ComposeConversation :contact-id="String(contact.id)">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
            />
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          :conversation-id="currentChat?.id"
          icon="i-lucide-phone"
          sm
          faded
          slate
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
        />
        <NextButton
          v-if="enableBankSimulation"
          v-tooltip.top-end="'Simulação do banco'"
          icon="i-lucide-landmark"
          slate
          faded
          sm
          :disabled="!contact.phone_number"
          @click="openBankSimulationModal"
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <ContactMergeModal :primary-contact="contact">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              sm
              :disabled="uiFlags.isMerging"
            />
          </template>
        </ContactMergeModal>
        <ContactDeleteModal
          v-if="isAdmin"
          :contact="contact"
          @deleted="$emit('panelClose')"
        >
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
              icon="i-ph-trash"
              slate
              faded
              sm
              ruby
              :disabled="uiFlags.isDeleting"
            />
          </template>
        </ContactDeleteModal>
      </div>
      <EditContact
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <NextDialog
        ref="bankSimulationDialog"
        width="6xl"
        :title="bankSimulationTitle"
        :description="bankSimulationDescription"
        overflow-y-auto
        sticky-header-footer
        :close-on-click-outside="false"
        @close="closeBankSimulationModal"
      >
        <ConnectedBanks
          v-if="showBankSimulationModal"
          :key="`${contact.id}-${bankSimulationConversationId || 'contact'}`"
          embedded
          :initial-contact="contact"
          :conversation-id="bankSimulationConversationId"
          @batch-change="bankSimulationBatch = $event"
          @busy-change="isConsultingBankSimulation = $event"
        />
        <section
          v-if="showBankSimulationModal"
          class="mt-6 space-y-4 rounded-xl border border-n-weak bg-n-alpha-1 p-5"
        >
          <p class="m-0 text-xs font-medium uppercase text-n-slate-10">
            {{ bankSimulationMessageLabel }}
          </p>
          <div v-if="bankSimulationOfferOptions.length" class="space-y-2">
            <label
              for="contact-bank-offer"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ bankSimulationOfferLabel }}
            </label>
            <NextSelect
              id="contact-bank-offer"
              v-model="bankSimulationOfferValue"
              class="w-full"
              :options="bankSimulationOfferOptions"
              placeholder="Selecione uma simulação"
              :aria-label="bankSimulationOfferLabel"
              :disabled="isConsultingBankSimulation || isSendingBankSimulation"
            />
          </div>
          <p
            v-if="hasSuccessfulBankSimulation"
            class="m-0 whitespace-pre-wrap text-sm leading-relaxed text-n-slate-12"
          >
            {{ bankSimulationMessage }}
          </p>
          <p v-else class="m-0 mt-2 text-sm text-n-slate-11">
            {{ bankSimulationNoSuccess }}
          </p>
          <p
            v-if="!contact.phone_number"
            class="m-0 mt-2 text-sm text-n-ruby-11"
          >
            {{ bankSimulationMissingPhone }}
          </p>
          <p
            v-else-if="!bankSimulationInbox"
            class="m-0 mt-2 text-sm text-n-ruby-11"
          >
            {{ bankSimulationMissingInbox }}
          </p>
        </section>
        <template #footer>
          <div class="flex flex-wrap justify-end gap-2">
            <NextButton
              :label="bankSimulationCancelLabel"
              color="slate"
              variant="ghost"
              @click="$refs.bankSimulationDialog.close()"
            />
            <NextButton
              :label="bankSimulationSendLabel"
              icon="i-lucide-send"
              :is-loading="isSendingBankSimulation"
              :disabled="!canSendBankSimulation"
              @click="sendBankSimulationMessage"
            />
          </div>
        </template>
      </NextDialog>
    </div>
  </div>
</template>
